import { DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { UserAvatar } from '../../../../components';
import styles from './CommentRow.module.scss';

const { Text } = Typography;

type Props = {
  // null name renders no name label (anonymized member comments on ideas).
  name: string | null;
  avatarUrl?: string | null;
  createdAt: string;
  body: string;
  index: number;
  // Optional chip rendered next to the name (e.g. an "Author" tag).
  badge?: ReactNode;
  canDelete: boolean;
  deleting: boolean;
  onDelete: () => void;
};

export const CommentRow = ({
  name,
  avatarUrl,
  createdAt,
  body,
  index,
  badge,
  canDelete,
  deleting,
  onDelete,
}: Props) => {
  return (
    <li className={styles.item} style={{ animationDelay: `${Math.min(index * 25, 200)}ms` }}>
      <UserAvatar size={24} avatarUrl={avatarUrl ?? undefined} />
      <div className={styles.itemBody}>
        <div className={styles.itemMeta}>
          {name ? <Text style={{ fontSize: 13, fontWeight: 500 }}>{name}</Text> : null}
          {badge}
          <Tooltip title={dayjs(createdAt).format('MMM D, YYYY h:mm A')}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(createdAt).fromNow()}
            </Text>
          </Tooltip>
          {canDelete ? (
            <Popconfirm
              title="Delete this comment?"
              description="This can't be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={onDelete}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                className={styles.itemDelete}
                aria-label="Delete comment"
                loading={deleting}
              />
            </Popconfirm>
          ) : null}
        </div>
        <div className={styles.itemText}>{body}</div>
      </div>
    </li>
  );
};
